import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import API from '../../api';
import { useLang } from '../../LangContext';
import ChatWindow from '../../components/ChatWindow';

const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

const TibebPattern = () => (
  <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, zIndex: 0, opacity: 0.04, pointerEvents: 'none' }}>
    <pattern id="tibeb" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
      <path d="M60 0 L120 60 L60 120 L0 60 Z" fill="none" stroke="currentColor" strokeWidth="0.5" />
      <circle cx="60" cy="60" r="15" fill="none" stroke="currentColor" strokeWidth="0.5" />
      <path d="M0 0 L120 120 M120 0 L0 120" stroke="currentColor" strokeWidth="0.2" opacity="0.5" />
    </pattern>
    <rect width="100%" height="100%" fill="url(#tibeb)" />
  </svg>
);

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState('success');
  const [showChat, setShowChat] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [lightbox, setLightbox] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [addedFeedback, setAddedFeedback] = useState(false);

  useEffect(() => {
    API.get('/bazaar/products/' + id)
      .then(r => setProduct(r.data))
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleKeyDown = useCallback((e) => {
    if (!lightbox) return;
    const photos = product?.photos || [];
    if (e.key === 'ArrowRight') setActivePhoto(p => (p + 1) % photos.length);
    if (e.key === 'ArrowLeft') setActivePhoto(p => (p - 1 + photos.length) % photos.length);
    if (e.key === 'Escape') setLightbox(false);
  }, [lightbox, product]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const showToast = (msg, type = 'success') => {
    setToast(msg);
    setToastType(type);
    setTimeout(() => setToast(''), 2500);
  };

  const handleAddToCart = () => {
    const token = localStorage.getItem('buyerToken') || localStorage.getItem('userToken');
    if (!token) { setShowLoginPrompt(true); return; }

    const prod = product;
    const key = 'bazaar_cart_' + prod.seller_id;
    let cart = [];
    try { cart = JSON.parse(localStorage.getItem(key)) || []; } catch { cart = []; }
    if (cart.length > 0 && cart[0].seller_id !== prod.seller_id) cart = [];

    const existing = cart.find(item => item.product_id === prod.id);
    if (existing) {
      existing.quantity += quantity;
    } else {
      cart.push({
        product_id: prod.id,
        name: prod.name,
        price: displayPrice,
        original_price: prod.price,
        quantity,
        photo_url: prod.photos?.[0]?.url || null,
        seller_id: prod.seller_id,
        seller_name: prod.seller_name,
      });
    }
    localStorage.setItem(key, JSON.stringify(cart));
    setAddedFeedback(true);
    setTimeout(() => setAddedFeedback(false), 2000);
    showToast(`${quantity} × ${prod.name} added to cart!`);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: product.name, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      showToast('Link copied to clipboard!');
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 100, color: 'var(--primary)', fontWeight: 700 }}>{t.loading}</div>;
  }

  if (notFound || !product) {
    return (
      <div className="page" style={{ paddingTop: 100 }}>
        <div className="hero-gradient" style={{ height: '30vh' }} />
        <Link to="/bazaar" style={{ color: 'var(--primary)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32 }}>{t.backToBazaar}</Link>
        <div style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🔍</div>
          <p style={{ color: 'var(--text-muted)' }}>{t.productNotFound || 'Product not found'}</p>
          <Link to="/bazaar" style={{ color: 'var(--primary)', fontWeight: 800, textDecoration: 'none', marginTop: 16, display: 'inline-block' }}>Browse Bazaar →</Link>
        </div>
      </div>
    );
  }

  const photos = product.photos || [];
  const outOfStock = Number(product.stock_quantity) === 0;
  const lowStock = !outOfStock && Number(product.stock_quantity) <= 5;
  const maxQty = Math.min(Number(product.stock_quantity) || 1, 20);
  const hasDiscount = product.discount_price && Number(product.discount_price) < Number(product.price);
  const displayPrice = hasDiscount ? Number(product.discount_price) : Number(product.price);

  return (
    <div className="page" style={{ paddingTop: 100 }}>
      <div className="hero-gradient" style={{ height: '30vh' }} />
      <TibebPattern />

      {/* Login prompt */}
      {showLoginPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(30, 41, 59, 0.8)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 24 }}
          onClick={() => setShowLoginPrompt(false)}>
          <div className="creative-card" style={{ padding: '48px 32px', maxWidth: 400, width: '100%', textAlign: 'center', border: 'none' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 48, marginBottom: 20 }}>🔐</div>
            <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 12 }}>Sign In Required</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 16, marginBottom: 32, lineHeight: 1.6 }}>You need to be logged in to add items to your cart.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button onClick={() => navigate('/login')} className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Sign In</button>
              <button onClick={() => navigate('/register/buyer')} className="btn-outline" style={{ width: '100%', justifyContent: 'center' }}>Register as Buyer</button>
              <button onClick={() => setShowLoginPrompt(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 8 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && photos.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(30, 41, 59, 0.95)', backdropFilter: 'blur(10px)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 24 }}
          onClick={() => setLightbox(false)}>
          <button onClick={() => setLightbox(false)} style={{ position: 'absolute', top: 32, right: 32, background: '#fff', border: 'none', width: 48, height: 48, borderRadius: '50%', fontSize: 24, cursor: 'pointer', zIndex: 1 }}>✕</button>
          <motion.img initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} src={photos[activePhoto]?.url} alt={product.name} style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 20, boxShadow: '0 50px 100px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Breadcrumb */}
      <nav style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 32, fontSize: 14, fontWeight: 700 }}>
        <Link to="/bazaar" style={{ color: 'var(--primary)', textDecoration: 'none' }}>Bazaar</Link>
        <span style={{ color: 'var(--text-muted)' }}>›</span>
        <Link to={`/bazaar/seller/${product.seller_id}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>{product.seller_name}</Link>
        <span style={{ color: 'var(--text-muted)' }}>›</span>
        <span style={{ color: 'var(--text-muted)' }}>{product.name}</span>
      </nav>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 0.8fr)', gap: 64, alignItems: 'start' }}>
        {/* Gallery */}
        <Reveal>
          <div style={{ position: 'sticky', top: 120 }}>
            <div 
              className="creative-card"
              onClick={() => photos.length > 0 && setLightbox(true)}
              style={{ padding: 0, overflow: 'hidden', cursor: 'zoom-in', aspectRatio: '1/1', background: 'var(--bg-subtle)' }}
            >
              {photos.length > 0 ? (
                <img
                  src={photos[activePhoto]?.url}
                  alt={product.name}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: outOfStock ? 0.5 : 1 }}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', background: 'var(--bg-subtle)' }} />
              )}
              {hasDiscount && (
                <div style={{ position: 'absolute', top: 16, left: 16, background: '#ef4444', color: '#fff', fontWeight: 900, fontSize: 16, padding: '8px 18px', borderRadius: 100, boxShadow: '0 8px 20px rgba(239, 68, 68, 0.3)' }}>
                  -{Math.round((1 - Number(product.discount_price) / Number(product.price)) * 100)}% OFF
                </div>
              )}
              {outOfStock && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(4px)' }}>
                  <span style={{ background: '#ef4444', color: '#fff', fontWeight: 900, fontSize: 20, padding: '12px 32px', borderRadius: 100, textTransform: 'uppercase' }}>Sold Out</span>
                </div>
              )}
            </div>

            {photos.length > 1 && (
              <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                {photos.map((ph, i) => (
                  <button
                    key={i}
                    onClick={() => setActivePhoto(i)}
                    style={{
                      width: 80, height: 80, borderRadius: 16, overflow: 'hidden', padding: 0, background: '#fff',
                      border: `2px solid ${activePhoto === i ? 'var(--primary)' : 'rgba(0,0,0,0.05)'}`,
                      transition: 'all 0.2s', cursor: 'pointer'
                    }}
                  >
                    <img src={ph.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </Reveal>

        {/* Info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          <Reveal delay={0.1}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
              <span className="section-tag" style={{ margin: 0 }}>{product.category}</span>
              <button onClick={handleShare} className="btn-outline" style={{ padding: '8px 20px', fontSize: 13 }}>Share</button>
            </div>

            <h1 style={{ fontSize: 'clamp(22px, 3.5vw, 30px)', fontWeight: 900, marginBottom: 16, color: 'var(--secondary)', lineHeight: 1.2 }}>{product.name}</h1>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 24 }}>
              <span style={{ fontSize: 36, fontWeight: 900, color: hasDiscount ? '#ef4444' : 'var(--primary)' }}>
                {hasDiscount ? (
                  <>
                    <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', fontSize: 24, fontWeight: 600, marginRight: 12 }}>ETB {Number(product.price).toLocaleString()}</span>
                    ETB {Number(product.discount_price).toLocaleString()}
                  </>
                ) : (
                  <>ETB {Number(product.price).toLocaleString()}</>
                )}
              </span>
              {lowStock && <span style={{ color: '#f59e0b', fontWeight: 800, fontSize: 14 }}>🔥 Only {product.stock_quantity} Left!</span>}
            </div>

            <div className="creative-card" style={{ padding: 24, background: 'var(--bg-subtle)', border: 'none' }}>
              <h3 style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Description</h3>
              <p style={{ fontSize: 16, color: 'var(--text-main)', lineHeight: 1.7, margin: 0 }}>{product.description || 'No description provided.'}</p>
            </div>

            {/* Package items */}
            {Number(product.is_package) === 1 && product.package_items && (
              <div className="creative-card" style={{ padding: 24, border: '1px solid rgba(139, 92, 246, 0.15)', background: 'rgba(139, 92, 246, 0.03)' }}>
                <h3 style={{ fontSize: 12, fontWeight: 800, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
                  📦 What's Included
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(() => {
                    try {
                      const items = typeof product.package_items === 'string' ? JSON.parse(product.package_items) : product.package_items;
                      return items.map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#ffffff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(139, 92, 246, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: '#8b5cf6', flexShrink: 0 }}>
                            {i + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, color: 'var(--secondary)', fontSize: 15 }}>{item.name}</div>
                          </div>
                          <div style={{ background: 'rgba(139, 92, 246, 0.08)', padding: '4px 12px', borderRadius: 20, fontWeight: 800, fontSize: 13, color: '#8b5cf6' }}>
                            ×{item.quantity}
                          </div>
                        </div>
                      ));
                    } catch { return null; }
                  })()}
                </div>
              </div>
            )}
          </Reveal>

          {/* Seller Card */}
          <Reveal delay={0.2}>
            <div className="creative-card" style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={{ width: 60, height: 60, background: 'var(--primary)', color: '#fff', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 900 }}>
                {product.seller_name?.charAt(0)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>Sold by</div>
                <Link to={`/bazaar/seller/${product.seller_id}`} style={{ fontSize: 18, fontWeight: 900, color: 'var(--secondary)', textDecoration: 'none' }}>{product.seller_name}</Link>
              </div>
              <Link to={`/bazaar/seller/${product.seller_id}`} className="btn-outline" style={{ padding: '10px 20px', fontSize: 13 }}>Store →</Link>
            </div>
          </Reveal>

          {/* Quantity & Actions */}
          {!outOfStock && (
            <Reveal delay={0.3}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 24 }}>
                <div>
                  <h3 style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 12 }}>Quantity</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button onClick={() => setQuantity(q => Math.max(1, q - 1))} style={qtyBtnStyle}>-</button>
                    <span style={{ fontSize: 20, fontWeight: 900, minWidth: 30, textAlign: 'center' }}>{quantity}</span>
                    <button onClick={() => setQuantity(q => Math.min(maxQty, q + 1))} style={qtyBtnStyle}>+</button>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 12 }}>Subtotal</h3>
                  <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--secondary)' }}>ETB {(displayPrice * quantity).toLocaleString()}</div>
                </div>
              </div>
            </Reveal>
          )}

          <Reveal delay={0.4}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {outOfStock ? (
                <div className="btn-outline" style={{ width: '100%', justifyContent: 'center', opacity: 0.5, cursor: 'not-allowed' }}>Sold Out</div>
              ) : (
                <button onClick={handleAddToCart} className="btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '18px 0', fontSize: 16 }}>
                  {addedFeedback ? '✓ Added to Cart' : `Add to Cart — ETB ${(displayPrice * quantity).toLocaleString()}`}
                </button>
              )}
              <button onClick={() => setShowChat(true)} className="btn-outline" style={{ width: '100%', justifyContent: 'center', padding: '16px 0' }}>
                💬 Chat with Seller
              </button>
            </div>
          </Reveal>

          {/* Delivery Info */}
          <Reveal delay={0.5}>
            <div className="grid-3">
              <InfoSmall icon="🚚" label="Delivery" sub="Direct from seller" />
              <InfoSmall icon="🛡️" label="Secure" sub="Verified Payment" />
              <InfoSmall icon="✅" label="Authentic" sub="Verified Seller" />
            </div>
          </Reveal>
        </div>
      </div>

      {toast && (
        <motion.div initial={{ y: 100 }} animate={{ y: 0 }} style={{ position: 'fixed', bottom: 40, right: 40, background: 'var(--secondary)', color: '#fff', padding: '16px 32px', borderRadius: 20, fontWeight: 800, zIndex: 9999 }}>
          {toast}
        </motion.div>
      )}

      {showChat && <ChatWindow sellerId={product.seller_id} sellerName={product.seller_name} productId={product.id} onClose={() => setShowChat(false)} />}
    </div>
  );
}

const InfoSmall = ({ icon, label, sub }) => (
  <div className="creative-card" style={{ padding: 16, textAlign: 'center' }}>
    <div style={{ fontSize: 24, marginBottom: 8 }}>{icon}</div>
    <div style={{ fontSize: 12, fontWeight: 900, color: 'var(--secondary)' }}>{label}</div>
    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700 }}>{sub}</div>
  </div>
);

const qtyBtnStyle = {
  width: 40, height: 40, borderRadius: 12, border: '1px solid rgba(0,0,0,0.1)', background: '#fff',
  fontSize: 20, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center'
};
